import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { companyInfo } from '../data/configuratorMock';
import { Menu, Heart, User, LogOut, LayoutDashboard, ChevronDown, Building2 } from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import { useAuth } from '../context/AuthContext';
import { useTenant, getTenantFromPath } from '../context/TenantContext';

const ConfiguratorHeader = ({ onViewDetails }) => {
  const { user, isAuthenticated, logout } = useAuth();
  const { tenant, branding, isTenantContext } = useTenant();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  
  // Get tenant slug from URL path for login link
  const tenantSlug = getTenantFromPath();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    setShowDropdown(false);
    navigate('/');
  };

  const handleDashboardClick = () => {
    setShowDropdown(false);
    navigate('/dashboard');
  };

  // Determine logo to display
  const logoUrl = branding?.logo_url || '/logo.png';
  const logoLink = branding?.logo_link_url || '/';
  const companyName = tenant?.name || 'Fusion Builder Pro';
  const primaryColor = branding?.primary_color || '#C41E3A';

  return (
    <header className="bg-white dark:bg-gray-900 transition-colors duration-300">
      {/* Main Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-800">
        {/* Logo */}
        <Link to={logoLink} className="flex items-center gap-3">
          {branding?.logo_url ? (
            <img 
              src={logoUrl} 
              alt={companyName}
              className="h-12 object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          {/* Fallback or default logo */}
          <div 
            className={`${branding?.logo_url ? 'hidden' : 'flex'} items-center gap-2`}
            style={{ display: branding?.logo_url ? 'none' : 'flex' }}
          >
            <div 
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: primaryColor }}
            >
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-bold text-gray-900 dark:text-white">
              {companyName}
            </span>
          </div>
        </Link>

        {/* Right Navigation */}
        <div className="flex items-center gap-6">
          <ThemeToggle />
          <button 
            onClick={onViewDetails}
            className="flex flex-col items-center text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 transition-colors group"
            data-testid="view-details-btn"
          >
            <Menu className="w-6 h-6" />
            <span className="text-xs mt-1 font-medium">View Details</span>
          </button>
          <button className="flex flex-col items-center text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 transition-colors group">
            <Heart className="w-6 h-6" />
            <span className="text-xs mt-1 font-medium">Saved Buildings</span>
          </button>
          
          {/* User Profile / Login */}
          {isAuthenticated ? (
            <div className="relative" ref={dropdownRef}>
              <button 
                onClick={() => setShowDropdown(!showDropdown)}
                className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                data-testid="user-profile-button"
              >
                <div 
                  className="w-9 h-9 rounded-full flex items-center justify-center text-white font-semibold text-sm"
                  style={{ backgroundColor: primaryColor }}
                >
                  {user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className="hidden sm:flex flex-col items-start">
                  <span className="text-sm font-medium truncate max-w-[120px]">{user?.name || 'User'}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[120px]">{user?.role_name || 'Staff'}</span>
                </div>
                <ChevronDown className={`w-4 h-4 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              {showDropdown && (
                <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50" data-testid="user-dropdown-menu">
                  <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{user?.name || 'User'}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email}</p>
                  </div>
                  <button
                    onClick={handleDashboardClick}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    data-testid="dashboard-link"
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    Go to Dashboard
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    data-testid="logout-button"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link 
              to={tenantSlug ? `/c/${tenantSlug}/login` : '/login'} 
              className="flex flex-col items-center text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 transition-colors group" 
              data-testid="sales-login-link"
            >
              <User className="w-6 h-6" />
              <span className="text-xs mt-1 font-medium">Sales Login</span>
            </Link>
          )}
        </div>
      </div>

      {/* Contact Bar - Show tenant name if on subdomain */}
      <div 
        className="text-white text-sm py-2 px-6 text-right"
        style={{ backgroundColor: primaryColor }}
      >
        {isTenantContext() ? (
          <>
            <span className="font-medium">{companyName}</span>
            {' | '}
          </>
        ) : null}
        Questions? Contact us at{' '}
        <a href={`tel:${companyInfo.phone}`} className="font-medium hover:underline">
          {companyInfo.phone}
        </a>
        {' '}or{' '}
        <a href={`mailto:${companyInfo.email}`} className="font-medium hover:underline">
          {companyInfo.email}
        </a>
      </div>
    </header>
  );
};

export default ConfiguratorHeader;
